import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import { Toaster, toast } from '@/components/ui/sonner';
import '@/App.css';
import '@/index.css';

// Pages
import MarketingHome from '@/pages/MarketingHome';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import Dashboard from '@/pages/Dashboard';
import ClientsPage from '@/pages/ClientsPage';
import ClientDetailPage from '@/pages/ClientDetailPage';
import ProjectsPage from '@/pages/ProjectsPage';
import ProjectDetailPage from '@/pages/ProjectDetailPage';
import ProjectArchivePage from '@/pages/ProjectArchivePage';
import InvoicesPage from '@/pages/InvoicesPage';
import AdminPage from '@/pages/AdminPage';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import SettingsPage from '@/pages/SettingsPage';
import NotificationsPage from '@/pages/NotificationsPage';
import OurWork from '@/pages/OurWork';
import ServicesPage from '@/pages/ServicesPage';
import ServicesDigitalPage from '@/pages/ServicesDigitalPage';
import ServicesEventsPage from '@/pages/ServicesEventsPage';
import ServicesSystemsPage from '@/pages/ServicesSystemsPage';
import ContactPage from '@/pages/ContactPage';
import MailingListPage from '@/pages/MailingListPage';
import PricingPage from '@/pages/PricingPage';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import TermsOfServicePage from '@/pages/TermsOfServicePage';
import FAQPage from '@/pages/FAQPage';
import LasVegasServicesPage from '@/pages/LasVegasServicesPage';
import GrowthAuditPage from '@/pages/GrowthAuditPage';
import PhilanthropyPage from '@/pages/PhilanthropyPage';
// Company Pages
import AboutPage from '@/pages/AboutPage';
import FounderPage from '@/pages/FounderPage';
import MissionPage from '@/pages/MissionPage';
import CaseStudiesPage from '@/pages/CaseStudiesPage';
// Resources Pages
import BlogPage from '@/pages/BlogPage';
import GuidesPage from '@/pages/GuidesPage';
import FADDsPage from '@/pages/FADDsPage';
import ToolsPage from '@/pages/ToolsPage';
import ConsultationPage from '@/pages/ConsultationPage';
// Guide Detail Pages
import GuideWebsiteLaunchPage from '@/pages/GuideWebsiteLaunchPage';
import GuideAIStarterPage from '@/pages/GuideAIStarterPage';
import GuideSecurityPage from '@/pages/GuideSecurityPage';
import GuideSEOPage from '@/pages/GuideSEOPage';
import GuideOnboardingPage from '@/pages/GuideOnboardingPage';
import GuideTransformationPage from '@/pages/GuideTransformationPage';
import GuideProjectManagementPage from '@/pages/GuideProjectManagementPage';
import GuideChangeManagementPage from '@/pages/GuideChangeManagementPage';
// Solutions Pages
import WebsiteRescuePage from '@/pages/WebsiteRescuePage';
import BusinessTurnaroundPage from '@/pages/BusinessTurnaroundPage';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState(null);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Periodic session check - verify user is still active every 15 seconds
  useEffect(() => {
    if (!token || !user) return;

    const checkSession = async () => {
      try {
        await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSessionError(null);
      } catch (error) {
        if (error.response?.status === 403) {
          // User has been disabled - force logout with notification
          const errorMessage = error.response?.data?.detail || 'Your session has ended.';
          toast.error('Account Disabled', {
            description: errorMessage,
            duration: 10000,
          });
          setSessionError(errorMessage);
          logout();
        }
      }
    };

    // Check session every 15 seconds
    const intervalId = setInterval(checkSession, 15000);

    return () => clearInterval(intervalId);
  }, [token, user]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      setSessionError(null);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      if (error.response?.status === 403) {
        setSessionError(error.response?.data?.detail || 'Your account has been disabled.');
      }
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    setSessionError(null);
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, first_name, last_name) => {
    setSessionError(null);
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      first_name,
      last_name,
      role: 'user'
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, sessionError, setSessionError }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-slate-gray">Loading...</div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
};

const ProtectedAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-slate-gray">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (user.role !== 'admin') {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<MarketingHome />} />
              <Route path="/our-work" element={<OurWork />} />
              <Route path="/our-services" element={<ServicesPage />} />
              <Route path="/services/digital" element={<ServicesDigitalPage />} />
              <Route path="/services/events" element={<ServicesEventsPage />} />
              <Route path="/services/systems" element={<ServicesSystemsPage />} />
              <Route path="/solutions/website-rescue" element={<WebsiteRescuePage />} />
              <Route path="/solutions/business-turnaround" element={<BusinessTurnaroundPage />} />
              <Route path="/our-pricing" element={<PricingPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/growth-audit" element={<GrowthAuditPage />} />
              <Route path="/philanthropy" element={<PhilanthropyPage />} />
              {/* Company Pages */}
              <Route path="/company/about" element={<AboutPage />} />
              <Route path="/company/founder" element={<FounderPage />} />
              <Route path="/company/mission" element={<MissionPage />} />
              <Route path="/company/case-studies" element={<CaseStudiesPage />} />
              {/* Resources Pages */}
              <Route path="/resources/blog" element={<BlogPage />} />
              <Route path="/resources/guides" element={<GuidesPage />} />
              <Route path="/resources/guides/website-launch" element={<GuideWebsiteLaunchPage />} />
              <Route path="/resources/guides/ai-starter" element={<GuideAIStarterPage />} />
              <Route path="/resources/guides/security-essentials" element={<GuideSecurityPage />} />
              <Route path="/resources/guides/local-seo" element={<GuideSEOPage />} />
              <Route path="/resources/guides/client-onboarding" element={<GuideOnboardingPage />} />
              <Route path="/resources/guides/digital-transformation" element={<GuideTransformationPage />} />
              <Route path="/resources/guides/project-management" element={<GuideProjectManagementPage />} />
              <Route path="/resources/guides/change-management" element={<GuideChangeManagementPage />} />
              <Route path="/resources/fadds" element={<FADDsPage />} />
              <Route path="/resources/tools" element={<ToolsPage />} />
              <Route path="/resources/consultation" element={<ConsultationPage />} />
              <Route path="/las-vegas" element={<LasVegasServicesPage />} />
              <Route path="/las-vegas-saas-development" element={<LasVegasServicesPage />} />
              <Route path="/las-vegas-web-development" element={<LasVegasServicesPage />} />
              <Route path="/nevada-it-services" element={<LasVegasServicesPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/clients"
              element={
                <ProtectedRoute>
                  <ClientsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/clients/:clientId"
              element={
                <ProtectedRoute>
                  <ClientDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/projects"
              element={
                <ProtectedRoute>
                  <ProjectsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/projects/:projectId"
              element={
                <ProtectedRoute>
                  <ProjectDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/archive"
              element={
                <ProtectedRoute>
                  <ProjectArchivePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/invoices"
              element={
                <ProtectedRoute>
                  <InvoicesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/invoices/:invoiceId"
              element={
                <ProtectedRoute>
                  <InvoicesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/notifications"
              element={
                <ProtectedRoute>
                  <NotificationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin"
              element={
                <ProtectedAdminRoute>
                  <AdminPage />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/dashboard/admin/mailing-list"
              element={
                <ProtectedAdminRoute>
                  <MailingListPage />
                </ProtectedAdminRoute>
              }
            />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;